import * as React from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import { ToolCallResultRendererQuery } from "./__generated__/ToolCallResultRendererQuery.graphql";
import { DefaultRendering } from "./DefaultRendering";
import { SubTaskResults } from "./SubTaskResults";

interface ToolCallResultRendererProps {
  conversationId: string; // GlobalID of the LlmConversation
  toolContent: string;
  sources: any[];
  renderAsSubTask?: boolean;
  subTaskTitle?: string;
}

const toolCallResultRendererQuery = graphql`
  query ToolCallResultRendererQuery($id: GlobalID!) {
    node(id: $id) {
      ... on GraphQLLlmConversation {
        id
        agentMessages {
          toolCalls {
            ...DefaultRendering_toolCalls
            ...SubTaskResults_lastToolCall
          }
        }
      }
    }
  }
`;

export const ToolCallResultRenderer: React.FC<ToolCallResultRendererProps> = ({
  conversationId,
  toolContent,
  sources,
  renderAsSubTask = false,
  subTaskTitle = "Perform Task",
}) => {
  const data = useLazyLoadQuery<ToolCallResultRendererQuery>(
    toolCallResultRendererQuery,
    { id: conversationId },
    { fetchPolicy: "store-or-network" } // Use cached data if available
  );

  const conversation = data.node;

  // TODO: This is not working as expected. We are relying on the parent conversation to trigger the refetch for now.
  // Set up Pusher subscription for real-time updates

  const getLastToolCall = () => {
    // Get the last tool call from agent messages
    const agentMessages = conversation?.agentMessages || [];
    for (let i = agentMessages.length - 1; i >= 0; i--) {
      const toolCalls = agentMessages[i]?.toolCalls;
      if (toolCalls && toolCalls.length > 0) {
        return toolCalls[toolCalls.length - 1];
      }
    }
    return null;
  };

  const getAllToolCalls = () => {
    // Collect all tool calls from agent messages
    const agentMessages = conversation?.agentMessages || [];
    const allToolCalls: any[] = [];
    for (const message of agentMessages) {
      if (message?.toolCalls) {
        allToolCalls.push(...message.toolCalls);
      }
    }
    return allToolCalls;
  };

  if (renderAsSubTask) {
    return (
      <SubTaskResults
        lastToolCall={getLastToolCall()}
        toolContent={toolContent}
        title={subTaskTitle}
        sources={sources}
      />
    );
  }

  return (
    <DefaultRendering
      toolCalls={getAllToolCalls()}
      toolContent={toolContent}
      sources={sources}
    />
  );
};